import { useMemo, useState } from "react";
import type { BrowseTrack } from "@touchscreen/api-client";
import {
  useRequestTrack,
  useBrowseGroups,
  useVenueState,
  requestErrorMessage,
} from "@touchscreen/hooks";
import { PlaylistRow } from "../components/PlaylistRow.js";
import { ConfirmRequestModal } from "../components/ConfirmRequestModal.js";
import { RequestConfirmedOverlay } from "../components/RequestConfirmedOverlay.js";
import { shuffled } from "../lib/shuffle.js";
import "./BrowseScreen.css";

export interface BrowseScreenProps {
  onBack: () => void;
  onSearch: () => void;
  /** Fires once a request has actually landed (success or accepted duplicate) to return to attract. */
  onRequested: () => void;
}

const PER_PAGE = 24;

export function BrowseScreen(props: BrowseScreenProps): React.JSX.Element {
  const { state: requestState, requestTrack, isCoolingDown, reset } = useRequestTrack();
  const { requestsDisabled } = useVenueState();

  // Groups only, up front — each group's tracks are queried by its own PlaylistRow, once that
  // row actually scrolls on screen. There's no per-playlist screen anymore; every group's tracks
  // live inline here as a horizontal scroller.
  const { data: groups, isError: groupsError } = useBrowseGroups();

  const [selectedTrack, setSelectedTrack] = useState<BrowseTrack | null>(null);
  const [confirmedTrack, setConfirmedTrack] = useState<BrowseTrack | null>(null);

  // Reshuffled each time this screen is entered fresh — presentation only, same deterministic API order.
  const shuffledGroups = useMemo(() => (groups ? shuffled(groups) : null), [groups]);

  function handleConfirm(): void {
    if (!selectedTrack) return;
    const track = selectedTrack;
    requestTrack(track.sm_id).then((result) => {
      if (result) {
        // duplicate:true is success too — same confirmation either way.
        reset();
        setSelectedTrack(null);
        setConfirmedTrack(track);
        setTimeout(() => {
          setConfirmedTrack(null);
          props.onRequested();
        }, 1500);
      }
    });
  }

  if (groupsError) {
    return (
      <div className="browse-screen browse-screen--message">
        <p>Browsing isn't available right now.</p>
        <button onClick={props.onBack}>Back</button>
      </div>
    );
  }

  return (
    <div className="browse-screen">
      <header className="browse-screen__header">
        <button className="browse-screen__back" onClick={props.onBack}>
          ← Back
        </button>
        <h1>Browse</h1>
        <button className="browse-screen__search" onClick={props.onSearch}>
          Search
        </button>
      </header>

      <div className="browse-screen__rows">
        {shuffledGroups === null
          ? Array.from({ length: 4 }, (_, i) => <div key={i} className="browse-screen__row-skeleton" />)
          : shuffledGroups.map((group) => (
              <PlaylistRow
                key={group.id}
                group={group}
                perPage={PER_PAGE}
                onSelectTrack={requestsDisabled ? undefined : setSelectedTrack}
              />
            ))}
      </div>

      {!requestsDisabled && selectedTrack ? (
        <ConfirmRequestModal
          track={selectedTrack}
          submitting={requestState.status === "loading"}
          disabledReason={isCoolingDown(selectedTrack.sm_id) ? "This track was just requested" : null}
          errorMessage={requestState.status === "error" ? requestErrorMessage(requestState.error) : null}
          onConfirm={handleConfirm}
          onCancel={() => {
            setSelectedTrack(null);
            reset();
          }}
        />
      ) : null}

      {confirmedTrack ? <RequestConfirmedOverlay track={confirmedTrack} /> : null}
    </div>
  );
}
